// Compactar imagem da Biblioteca — regras puras da tela (MediaCompressDialog). O backend é a
// fonte das regras (domain/media_compression.go); aqui só o que a tela precisa para montar o
// pedido, mostrar o resultado e comparar as duas imagens.
import type { MediaAsset, MediaCompression, MediaCompressionMeta, MediaCompressionPresetKey, MediaCompressionRequest } from '@/types/media';
import { formatBytes } from './formatters';

/** Só imagem pronta e que não seja GIF (pode ser animado). */
export const canCompressMedia = (m: Pick<MediaAsset, 'kind' | 'status' | 'extension' | 'content_type'>): boolean =>
  m.kind === 'image' &&
  m.status === 'ready' &&
  (m.extension || '').toLowerCase() !== '.gif' &&
  (m.content_type || '').toLowerCase() !== 'image/gif';

/** Acima disto a imagem é sinalizada como grande (a cópia pública do CDN vai sem conversão até 1,5 MB). */
export const LARGE_IMAGE_BYTES = 1024 * 1024;

/** Ainda esperando o worker. */
export const isCompressionRunning = (status?: string): boolean => status === 'pending' || status === 'processing';

/** "−63%" (sinal de menos tipográfico). */
export const formatGain = (percent: number): string => `−${Math.max(0, Math.round(percent))}%`;

/** "3,1 MB → 820 KB (−74%)". */
export const compressionSummary = (c: Pick<MediaCompression, 'original_size_bytes' | 'size_bytes' | 'gain_percent'>): string =>
  c.size_bytes === undefined
    ? formatBytes(c.original_size_bytes)
    : `${formatBytes(c.original_size_bytes)} → ${formatBytes(c.size_bytes)} (${formatGain(c.gain_percent)})`;

/** "2560×1920 · JPG" */
export const imageSpec = (width?: number, height?: number, typeOrExt?: string): string => {
  const fmt = (typeOrExt || '')
    .replace(/^image\//, '')
    .replace(/^\./, '')
    .replace('jpeg', 'jpg')
    .toUpperCase();
  return [width && height ? `${width}×${height}` : '', fmt].filter(Boolean).join(' · ');
};

export interface CompressionForm {
  preset: MediaCompressionPresetKey;
  quality: number;
  /** Texto do campo "lado maior" — vazio mantém o tamanho. */
  maxSide: string;
}

/**
 * Monta o pedido. Personalizada: qualidade e lado maior dentro dos limites do backend (lado vazio
 * ou 0 = mantém o tamanho). Devolve a mensagem de erro em vez do pedido quando inválido.
 */
export const buildCompressionRequest = (form: CompressionForm, meta: Pick<MediaCompressionMeta, 'min_quality' | 'max_quality' | 'min_side'>):
  | { request: MediaCompressionRequest; error?: undefined }
  | { request?: undefined; error: string } => {
  if (form.preset !== 'custom') return { request: { preset: form.preset } };
  const q = Math.round(form.quality);
  if (!Number.isFinite(q) || q < meta.min_quality || q > meta.max_quality) {
    return { error: `A qualidade deve ficar entre ${meta.min_quality} e ${meta.max_quality}.` };
  }
  const raw = form.maxSide.replace(/\D/g, '');
  const side = raw ? Number(raw) : 0;
  if (side !== 0 && (side < meta.min_side || side > 20000)) {
    return { error: `O lado maior deve ficar entre ${meta.min_side} e 20000 px (ou vazio para manter o tamanho).` };
  }
  return { request: { preset: 'custom', quality: q, max_side: side } };
};

/** Zoom da comparação: "fit" ajusta à tela; os demais mostram as duas no mesmo tamanho em px. */
export type CompareZoom = 'fit' | '100' | '200';

/**
 * Largura (px) em que as duas imagens são desenhadas no zoom: a da versão compactada × zoom. A
 * original, quando maior, é reduzida à mesma largura — assim o mesmo ponto fica no mesmo lugar
 * nos dois quadros e a rolagem pode ser sincronizada.
 */
export const compareWidth = (zoom: CompareZoom, candidateWidth?: number, originalWidth?: number): number | undefined => {
  if (zoom === 'fit') return undefined;
  const base = candidateWidth || originalWidth;
  if (!base) return undefined;
  return base * (zoom === '200' ? 2 : 1);
};

/** Posição proporcional da rolagem no outro quadro (os quadros podem ter alturas diferentes). */
export const syncedScroll = (pos: number, max: number, otherMax: number): number => {
  if (max <= 0 || otherMax <= 0) return 0;
  return Math.round((Math.min(Math.max(pos, 0), max) / max) * otherMax);
};
